import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, Calendar, Check, Clock, Share2, User } from "lucide-react";
import { toast } from "sonner";
import { BlogRenderer } from "../../components/blog/BlogRenderer";
import { getPublishedBlog, PublicBlog } from "../../../services/blog.service";

const formatDate = (value: string | null) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

const formatAuthor = (name: string | null) => {
  if (!name) return "MORROCARS Editorial";
  const parts = String(name).split(/\s+/).filter(Boolean);
  const clean = parts.filter(p => !p.includes("==")).join(" ");
  if (clean && clean.length <= 40) return clean;
  return "MORROCARS Editorial";
};

const getReadTime = (content: any) => {
  if (!content) return "1 min read";
  try {
    const extractText = (node: any): string => {
      if (!node) return "";
      if (typeof node === "string") return node;
      if (node.text) return node.text;
      if (Array.isArray(node.content)) {
        return node.content.map(extractText).join(" ");
      }
      return "";
    };
    const plainText = extractText(content).trim();
    const words = plainText.length > 0 ? plainText.split(/\s+/).length : 0;
    const minutes = Math.max(1, Math.ceil(words / 200));
    return `${minutes} min read`;
  } catch {
    return "2 min read";
  }
};

export function BlogDetail() {
  const { slug = "" } = useParams();
  const [blog, setBlog] = useState<PublicBlog | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!slug) return;
    getPublishedBlog(slug)
      .then(setBlog)
      .catch(() => setBlog(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Article link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-[#717182]">
        Loading article…
      </div>
    );
  if (!blog)
    return (
      <div className="min-h-screen bg-white max-w-4xl mx-auto px-4 py-16">
        <Link to="/blog" className="text-[#1E6DFF]">
          ← Back to Blog
        </Link>
        <h1 className="text-3xl text-[#2D323A] mt-8">Article not found</h1>
      </div>
    );

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className="bg-white min-h-screen">
      <article className="max-w-4xl mx-auto px-4 py-10">
        <Link
          to="/blog"
          className="flex items-center gap-2 text-[#717182] hover:text-[#1E6DFF] mb-9 text-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>
        <header className="mb-8">
          <span className="blog-category">
            {blog.Category || "Buying Guide"}
          </span>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1f2937] mt-4 mb-5 leading-snug">
            {blog.Title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-[#717182]">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {formatAuthor(blog.CreatedBy)}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(blog.PublishedAt)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {getReadTime(blog.Content)}
            </span>
          </div>

          {/* Social Share Buttons */}
          <div className="flex flex-wrap items-center gap-2.5 mt-5 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 text-xs md:text-sm font-medium transition-colors cursor-pointer shadow-xs"
              title="Copy article link"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Share2 className="w-3.5 h-3.5 text-gray-500" />}
              <span>{copied ? "Copied!" : "Share Link"}</span>
            </button>

            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#1877F2] text-white hover:bg-[#166fe5] text-xs md:text-sm font-medium transition-colors shadow-xs"
              title="Share on Facebook"
            >
              <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span>Facebook</span>
            </a>

            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(blog.Title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#1DA1F2] text-white hover:bg-[#1a94df] text-xs md:text-sm font-medium transition-colors shadow-xs"
              title="Share on Twitter"
            >
              <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <span>Twitter/X</span>
            </a>

            <a
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(blog.Title + " " + shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#25D366] text-white hover:bg-[#20bd5a] text-xs md:text-sm font-medium transition-colors shadow-xs"
              title="Share on WhatsApp"
            >
              <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.301-.15-1.78-.878-2.056-.979-.276-.1-.477-.15-.678.15-.2.301-.778.979-.953 1.18-.175.2-.351.226-.652.075-.301-.15-1.272-.469-2.424-1.496-.897-.799-1.503-1.786-1.68-2.087-.175-.301-.019-.464.132-.614.136-.135.301-.351.452-.527.15-.175.2-.301.301-.502.1-.2.05-.376-.025-.527-.075-.15-.678-1.632-.929-2.235-.245-.588-.493-.509-.678-.518-.175-.01-.376-.01-.577-.01-.201 0-.527.075-.803.376s-1.054 1.029-1.054 2.51c0 1.48 1.079 2.909 1.23 3.109.15.2 2.122 3.24 5.14 4.544.718.31 1.278.495 1.716.634.721.23 1.378.197 1.897.12.578-.087 1.78-.727 2.031-1.43.251-.703.251-1.305.176-1.43-.075-.126-.276-.201-.577-.351zM12.04 21.84a9.78 9.78 0 01-4.99-1.365l-.358-.212-3.71.973.99-3.616-.233-.371A9.78 9.78 0 012.25 12.04c0-5.4 4.39-9.79 9.79-9.79 5.4 0 9.79 4.39 9.79 9.79 0 5.401-4.39 9.8-9.79 9.8z"/>
              </svg>
              <span>WhatsApp</span>
            </a>
          </div>
        </header>
        {blog.CoverImage && (
          <img
            src={blog.CoverImage}
            alt={blog.Title}
            className="w-full max-h-[460px] object-cover rounded-2xl mb-10"
          />
        )}
        {blog.Content && <BlogRenderer content={blog.Content} />}

        <div className="bg-[#1860ec] rounded-2xl py-7 px-6 mt-12 text-white text-center shadow-md">
          <h3 className="text-lg md:text-xl font-bold mb-1.5 leading-tight tracking-tight">
            Ready to Find Your Perfect Vehicle?
          </h3>
          <p className="text-white/85 text-xs md:text-sm mb-5 leading-normal">
            Browse thousands of verified listings across Morocco
          </p>
          <Link
            to="/search"
            className="inline-flex items-center justify-center px-6 py-2 bg-white text-[#1860ec] rounded-full font-semibold text-xs md:text-sm hover:bg-slate-50 transition-colors shadow-sm"
          >
            Browse Vehicles
          </Link>
        </div>
      </article>
    </div>
  );
}
